import { useState } from 'react'
import moment from 'moment'
import { FormInstance } from 'antd/lib/form/Form'
import { Button, DatePicker, Form, InputGroup, Select } from 'components/common'
import {
  CustomerConditionType,
  CustomerQueryType,
  OrdersQueryVariables,
  SortType,
} from 'lib/graphql/storeadmin'
import { Input } from 'antd'

const DateOperatorSet = {
  Equal: 'bookingDates',
  Gte: 'bookingDates_Gte',
  Lte: 'bookingDates_Lte',
}

const CustomerFieldSet = {
  LastName: 'last_name',
  FirstName: 'first_name',
  Phone: 'phone',
  Email: 'email',
}

const SortField = {
  orderNumber: '予約番号',
  created_at: '予約作成日',
  bookingDates__date: '予約対象日',
}

export interface BookingsFilterFormValues {
  bookingDate?: moment.Moment
  dateOperator?: string
  product?: string
  customer: {
    field?: string
    value?: string
  }
  sort: {
    field: string
    type: SortType
  }
}

export interface BookingsFilterFormProps {
  onFiltering: (variables: Partial<OrdersQueryVariables>) => Promise<void>
  form: FormInstance<BookingsFilterFormValues>
  onSearchClear?: () => void
}

export const BookingsFilterForm = ({
  onFiltering,
  form,
  onSearchClear,
}: BookingsFilterFormProps): JSX.Element => {
  const [showSearchButton, setSearchButton] = useState(false)
  const [showSearchEmptyMessage, setSearchEmptyMessage] = useState(null)

  const onFinish = async (values: BookingsFilterFormValues) => {
    const variables: Partial<OrdersQueryVariables> = {}

    Object.entries(DateOperatorSet).forEach(([_, value]) => {
      if (value === values['dateOperator']) {
        variables[value] = values['bookingDate']
          ? values['bookingDate'].format('YYYY-MM-DD')
          : null
      } else variables[value] = null
    })

    variables.productName_Contains = values['product']
      ? values['product']
      : null

    if (
      values['customer']['value'] &&
      values['customer']['field'] === CustomerFieldSet.LastName
    ) {
      variables.customerQuery = [
        {
          field: 'last_name',
          value: values['customer']['value']?.replace(/\s/g, ''),
          condition: CustomerConditionType.Contains,
          type: CustomerQueryType.Or,
        },
      ]
    } else if (
      values['customer']['value'] &&
      values['customer']['field'] === CustomerFieldSet.FirstName
    ) {
      variables.customerQuery = [
        {
          field: 'first_name',
          value: values['customer']['value']?.replace(/\s/g, ''),
          condition: CustomerConditionType.Contains,
          type: CustomerQueryType.Or,
        },
      ]
    } else if (
      values['customer']['value'] &&
      values['customer']['field'] !== CustomerFieldSet.LastName &&
      values['customer']['field'] !== CustomerFieldSet.FirstName
    ) {
      variables.customerQuery = [
        {
          field: values['customer']['field'],
          value: values['customer']['value']?.replace(/\s/g, ''),
          condition: CustomerConditionType.Contains,
          type: CustomerQueryType.Or,
        },
      ]
    } else {
      variables.customerQuery = []
    }

    variables.sort = {
      field: values.sort.field,
      type: values.sort.type,
    }

    await onFiltering(variables)
  }

  return (
    <>
      {showSearchEmptyMessage !== null && (
        <div className="ml-2 text-red-600">{showSearchEmptyMessage}</div>
      )}
      <Form
        form={form}
        component="form"
        onFinish={onFinish}
        onValuesChange={(_, values) => {
          const checkValues =
            typeof values?.bookingDate !== 'undefined' ||
            values?.customer?.value !== '' ||
            values?.product !== ''
          if (checkValues) {
            setSearchEmptyMessage(null)
            setSearchButton(true)
          } else {
            setSearchButton(false)
          }
        }}
      >
        <Form.Item>
          <InputGroup style={{ display: 'flex' }}>
            <Form.Item
              name={['sort', 'field']}
              noStyle
              initialValue="orderNumber"
            >
              <Select
                onChange={() => {
                  onFinish(form.getFieldsValue(true))
                }}
              >
                {Object.entries(SortField).map(([key, value]) => (
                  <Select.Option key={key} value={key}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name={['sort', 'type']}
              noStyle
              initialValue={SortType.Desc}
            >
              <Select
                onChange={() => {
                  onFinish(form.getFieldsValue(true))
                }}
              >
                <Select.Option value={SortType.Asc}>昇順</Select.Option>
                <Select.Option value={SortType.Desc}>降順</Select.Option>
              </Select>
            </Form.Item>
          </InputGroup>
        </Form.Item>
        <div className="flex space-x-8">
          <Form.Item name="product" initialValue="">
            <Input autoComplete="off" placeholder="施設名で絞り込む" />
          </Form.Item>
          <Form.Item>
            <InputGroup style={{ display: 'flex' }} compact>
              <Form.Item
                name={['customer', 'field']}
                noStyle
                initialValue={CustomerFieldSet.LastName}
              >
                <Select>
                  <Select.Option value={CustomerFieldSet.LastName}>
                    姓
                  </Select.Option>
                  <Select.Option value={CustomerFieldSet.FirstName}>
                    名
                  </Select.Option>
                  <Select.Option value={CustomerFieldSet.Phone}>
                    電話番号
                  </Select.Option>
                  <Select.Option value={CustomerFieldSet.Email}>
                    メールアドレス
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name={['customer', 'value']} noStyle initialValue="">
                <Input autoComplete="off" placeholder="お客様情報で絞り込む" />
              </Form.Item>
            </InputGroup>
          </Form.Item>
          <div className="flex">
            <Form.Item name="dateOperator" initialValue={DateOperatorSet.Equal}>
              <Select>
                <Select.Option value={DateOperatorSet.Equal}>
                  利用予定日と一致
                </Select.Option>
                <Select.Option value={DateOperatorSet.Gte}>
                  利用予定日以降
                </Select.Option>
                <Select.Option value={DateOperatorSet.Lte}>
                  利用予定日以前
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="bookingDate">
              <DatePicker allowClear />
            </Form.Item>
          </div>
          <div className="flex">
            <Form.Item>
              {showSearchButton ? (
                <Button htmlType="submit">絞り込む</Button>
              ) : (
                <Button
                  onClick={() =>
                    setSearchEmptyMessage('絞り込みたい内容を入力してください')
                  }
                >
                  絞り込む
                </Button>
              )}
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => {
                  showSearchButton && onSearchClear()
                  setSearchButton(false)
                  setSearchEmptyMessage(null)
                }}
              >
                クリア
              </Button>
            </Form.Item>
          </div>
        </div>
      </Form>
    </>
  )
}
